import React, { useState } from "react";
import styled from "styled-components";
import DeleteButton from "./DeleteButton";

export interface OrderEntry {
  id: string;
  created_at: string;
  product_id: string | null;
  recipient_name: string | null;
  postcode: string | null;
  address: string | null;
  address_detail: string | null;
  phone: string | null;
  payment_method: string | null;
  bank_name: string | null;
  account_number: string | null;
  total_amount: string | null;
}

interface AdminOrdersProps {
  entries: OrderEntry[];
  visibleEntries: number;
  isLoading: boolean;
  onShowMore: () => void;
  onRefresh: () => void;
  onDelete: (entryId: string) => void | Promise<void>;
  formatDateForDisplay: (date: string) => string;
}

const formatFullAddress = (entry: OrderEntry): string => {
  const parts = [
    entry.postcode ? `[${entry.postcode}]` : "",
    entry.address ?? "",
    entry.address_detail ?? "",
  ].filter(Boolean);
  return parts.join(" ").trim() || "—";
};

const AdminOrders: React.FC<AdminOrdersProps> = ({
  entries,
  visibleEntries,
  isLoading,
  onShowMore,
  onRefresh,
  onDelete,
  formatDateForDisplay,
}) => {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopy = async (key: string, value: string | null) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedId(key);
      setTimeout(() => setCopiedId(null), 1200);
    } catch (err) {
      console.error("복사 실패:", err);
    }
  };

  const visible = entries.slice(0, visibleEntries);
  const hasMore = entries.length > visibleEntries;

  return (
    <Section>
      <Header>
        <h2>주문 정보</h2>
        <HeaderRight>
          <Count>총 {entries.length}건</Count>
          <RefreshButton onClick={onRefresh} disabled={isLoading}>
            {isLoading ? "불러오는 중..." : "새로고침"}
          </RefreshButton>
        </HeaderRight>
      </Header>

      {entries.length === 0 ? (
        <EmptyState>
          {isLoading ? "불러오는 중..." : "아직 접수된 주문이 없어요"}
        </EmptyState>
      ) : (
        <>
          <DataTable>
            <thead>
              <tr>
                <th style={{ width: "180px" }}>접수 시각</th>
                <th style={{ width: "120px" }}>받는 이</th>
                <th>배송지</th>
                <th style={{ width: "150px" }}>전화번호</th>
                <th style={{ width: "180px" }}>결제수단</th>
                <th style={{ width: "120px" }}>금액</th>
                <th style={{ width: "120px", textAlign: "center" }}>복사</th>
                <th style={{ width: "80px", textAlign: "center" }}>삭제</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((entry) => {
                const fullAddress = formatFullAddress(entry);
                return (
                  <tr key={entry.id}>
                    <td>{formatDateForDisplay(entry.created_at)}</td>
                    <td>
                      <Mono>{entry.recipient_name ?? "—"}</Mono>
                    </td>
                    <td>
                      <Mono>{fullAddress}</Mono>
                    </td>
                    <td>
                      <Mono>{entry.phone ?? "—"}</Mono>
                    </td>
                    <td>
                      <Mono>
                        {entry.payment_method ?? "—"}
                        {entry.bank_name ? ` / ${entry.bank_name}` : ""}
                        {entry.account_number ? ` / ${entry.account_number}` : ""}
                      </Mono>
                    </td>
                    <td>
                      <Mono>{entry.total_amount ?? "—"}</Mono>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <ButtonRow>
                        <CopyButton
                          type="button"
                          onClick={() => handleCopy(`addr-${entry.id}`, fullAddress)}
                          disabled={fullAddress === "—"}
                        >
                          {copiedId === `addr-${entry.id}` ? "복사됨" : "주소"}
                        </CopyButton>
                        <CopyButton
                          type="button"
                          variant="danger"
                          onClick={() => handleCopy(`phone-${entry.id}`, entry.phone)}
                          disabled={!entry.phone}
                        >
                          {copiedId === `phone-${entry.id}` ? "복사됨" : "전화"}
                        </CopyButton>
                      </ButtonRow>
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <DeleteButton
                        type="button"
                        onClick={() => onDelete(entry.id)}
                      >
                        삭제
                      </DeleteButton>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </DataTable>

          {hasMore && (
            <ShowMoreButton onClick={onShowMore}>
              더 보기 ({entries.length - visibleEntries}건 남음)
            </ShowMoreButton>
          )}
        </>
      )}
    </Section>
  );
};

const Section = styled.section`
  background: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 30px;

  h2 {
    margin: 0;
    color: #2c3e50;
    font-size: 24px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 15px;
  margin-bottom: 25px;
  gap: 16px;
  flex-wrap: wrap;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Count = styled.span`
  color: #495057;
  font-size: 14px;
  font-weight: 600;
`;

const RefreshButton = styled.button`
  background-color: #27c360;
  color: white;
  border: none;
  padding: 10px 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #229a4c;
    transform: translateY(-1px);
  }

  &:disabled {
    background-color: #adb5bd;
    cursor: not-allowed;
    transform: none;
  }
`;

const EmptyState = styled.div`
  padding: 60px 20px;
  text-align: center;
  color: #6c757d;
  font-size: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
`;

const DataTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  th {
    background-color: #f8f9fa;
    font-weight: 600;
    color: #495057;
    font-size: 14px;
    padding: 14px 16px;
    text-align: left;
    border-bottom: 1px solid #e9ecef;
  }

  td {
    padding: 14px 16px;
    border-bottom: 1px solid #e9ecef;
    color: #2c3e50;
    font-size: 13px;
    vertical-align: middle;
  }

  tr:last-child td {
    border-bottom: none;
  }

  tr:hover td {
    background-color: #f8f9fa;
  }
`;

const Mono = styled.span`
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 13px;
  word-break: break-all;
`;

const ButtonRow = styled.div`
  display: inline-flex;
  gap: 6px;
`;

interface CopyButtonProps {
  variant?: "danger";
}

const CopyButton = styled.button<CopyButtonProps>`
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 600;
  border: none;
  border-radius: 6px;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${(props) =>
    props.variant === "danger" ? "#dc3545" : "#0d6efd"};

  &:hover {
    background-color: ${(props) =>
      props.variant === "danger" ? "#c82333" : "#0b5ed7"};
    transform: translateY(-1px);
  }

  &:disabled {
    background-color: #adb5bd;
    cursor: not-allowed;
    transform: none;
  }
`;

const ShowMoreButton = styled.button`
  margin: 20px auto 0;
  display: block;
  background-color: #6c757d;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #5a6268;
    transform: translateY(-1px);
  }
`;

export default AdminOrders;
